import { Box, Button, Tab, Tabs } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Schedules from "./NewSchedules";
import { TabPanel } from "../../../components/TabPanel";
import { defaultScrollTheme } from "../../../utils/utils";
import PagesContainer from "../../../components/UI/PagesContainer";
import { useExtensionConfigs } from "../../../hooks/useExtensionConfigs";
import { Extension } from "../../../models/intalled-extensions";
import { ITicketAutomationReceivedRuleAllowedAction } from "../../../services/tasySchedMenu.service";

interface IMySchedulesProps {
  isLinkAccess?: boolean;
  basePath?: string;
  newSchedulePath?: string;
}

const a11yProps = (index: number) => ({
  id: `schedule-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const NewMySchedules = ({
  isLinkAccess,
  basePath = "/schedule",
  newSchedulePath = "/schedule/newSchedule",
}: IMySchedulesProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { fetchExtensionConfig } = useExtensionConfigs();
  const [index, setIndex] = useState(0);
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const isNewScheduleFlow = pathname.startsWith("/schedule-new");
  const locationState = state as {
    allowedAction?: ITicketAutomationReceivedRuleAllowedAction | null;
    ticketAutomationReceivedRuleId?: string | null;
  } | null;
  const locationAllowedAction = locationState?.allowedAction;
  const locationRuleId = locationState?.ticketAutomationReceivedRuleId;
  const [storedAllowedAction, setStoredAllowedAction] =
    useState<ITicketAutomationReceivedRuleAllowedAction | null>(() => {
      if (!isNewScheduleFlow) return null;

      const record = sessionStorage.getItem("scheduleNewMySchedulesAction");
      return record ? JSON.parse(record) : null;
    });
  const [storedRuleId, setStoredRuleId] = useState<string | null>(() => {
    if (!isNewScheduleFlow) return null;
    return sessionStorage.getItem("scheduleNewMySchedulesRuleId");
  });

  const allowedAction = useMemo(
    () =>
      isNewScheduleFlow ? (locationAllowedAction ?? storedAllowedAction) : null,
    [isNewScheduleFlow, locationAllowedAction, storedAllowedAction],
  );
  const ticketAutomationReceivedRuleId = isNewScheduleFlow
    ? (locationRuleId ?? storedRuleId)
    : null;

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => setIndex(newValue),
    [],
  );

  const onClickAddButton = () => navigate(newSchedulePath);
  const onClickScheduleButton = () => {
    window.open(
      `https://wa.me/${whatsAppNumber}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const fetchWhatsAppNumber = useCallback(async () => {
    const configWhatsAppNumber = await fetchExtensionConfig(
      Extension.MY_SCHEDULES,
      "WhatsAppNumber",
    );
    if (configWhatsAppNumber?.value) {
      setWhatsAppNumber(configWhatsAppNumber.value);
    }
  }, [fetchExtensionConfig]);

  useEffect(() => {
    fetchWhatsAppNumber();
  }, [fetchWhatsAppNumber]);

  useEffect(() => {
    if (!isNewScheduleFlow || !locationAllowedAction) return;
    sessionStorage.setItem(
      "scheduleNewMySchedulesAction",
      JSON.stringify(locationAllowedAction),
    );
    setStoredAllowedAction(locationAllowedAction);
  }, [isNewScheduleFlow, locationAllowedAction]);

  useEffect(() => {
    if (!isNewScheduleFlow || !locationRuleId) return;
    sessionStorage.setItem("scheduleNewMySchedulesRuleId", locationRuleId);
    setStoredRuleId(locationRuleId);
  }, [isNewScheduleFlow, locationRuleId]);

  return (
    <PagesContainer
      title={t("page.schedules.mySchedules.title")}
      subtitle={t("page.schedules.mySchedules.subtitle")!}
      hideBackButton={isLinkAccess}
      rowActions={
        <>
          {!isLinkAccess &&
            (!isNewScheduleFlow || allowedAction?.newSchedule) && (
              <Button variant="contained" onClick={onClickAddButton}>
                {t("page.schedules.mySchedules.add")}
              </Button>
            )}
          {isLinkAccess && whatsAppNumber && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Button variant="contained" onClick={onClickScheduleButton}>
                {t("page.schedules.mySchedules.schedule")}
              </Button>
            </Box>
          )}
        </>
      }
    >
      <Tabs value={index} onChange={handleChange}>
        <Tab label={t("page.schedules.mySchedules.next")} {...a11yProps(0)} />
        <Tab
          label={t("page.schedules.mySchedules.historic")}
          {...a11yProps(1)}
        />
      </Tabs>
      <Box
        height="calc(100% - 48px)"
        overflow="auto"
        sx={defaultScrollTheme}
      >
        <TabPanel index={0} value={index} sx={{ padding: 0, paddingRight: 1 }}>
          <Schedules
            isLinkAccess={isLinkAccess}
            basePath={basePath}
            allowedAction={allowedAction}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
          />
        </TabPanel>
        <TabPanel index={1} value={index} sx={{ padding: 0, paddingRight: 1 }}>
          <Schedules
            isLinkAccess={isLinkAccess}
            historic
            basePath={basePath}
            allowedAction={allowedAction}
          />
        </TabPanel>
      </Box>
    </PagesContainer>
  );
};

export default NewMySchedules;
